<script>
import NextButton from 'dashboard/components-next/button/Button.vue';

export default {
  name: 'FilterInput',
  components: {
    NextButton,
  },
  props: {
    modelValue: {
      type: Object,
      default: () => {},
    },
    filterAttributes: {
      type: Array,
      default: () => [],
    },
    inputType: {
      type: String,
      default: 'plain_text',
    },
    operators: {
      type: Array,
      default: () => [],
    },
    dropdownValues: {
      type: Array,
      default: () => [],
    },
    showQueryOperator: {
      type: Boolean,
      default: false,
    },
    showUserInput: {
      type: Boolean,
      default: true,
    },
    groupedFilters: {
      type: Boolean,
      default: false,
    },
    filterGroups: {
      type: Array,
      default: () => [],
    },
    customAttributeType: {
      type: String,
      default: '',
    },
    errorMessage: {
      type: String,
      default: '',
    },
  },
  emits: ['update:modelValue', 'removeFilter', 'resetFilter'],
  computed: {
    selectedAttributeTitle() {
      if (this.groupedFilters && this.filterGroups?.length) {
        for (const group of this.filterGroups) {
          const attr = group.attributes?.find(a => a.key === this.attributeKey);
          if (attr) return attr.name || '';
        }
        return '';
      }
      const attr = this.filterAttributes.find(a => a.key === this.attributeKey);
      return attr?.name || '';
    },
    selectedOperatorTitle() {
      const op = this.operators.find(o => o.value === this.filterOperator);
      if (!op?.value) return '';
      return this.$t(`FILTER.OPERATOR_LABELS.${op.value}`);
    },
    attributeKey: {
      get() {
        if (!this.modelValue) return null;
        return this.modelValue.attribute_key;
      },
      set(value) {
        const payload = this.modelValue || {};
        this.$emit('update:modelValue', { ...payload, attribute_key: value });
      },
    },
    filterOperator: {
      get() {
        if (!this.modelValue) return null;
        return this.modelValue.filter_operator;
      },
      set(value) {
        const payload = this.modelValue || {};
        this.$emit('update:modelValue', { ...payload, filter_operator: value });
      },
    },
    values: {
      get() {
        if (!this.modelValue) return null;
        return this.modelValue.values;
      },
      set(value) {
        const payload = this.modelValue || {};
        this.$emit('update:modelValue', { ...payload, values: value });
      },
    },
    query_operator: {
      get() {
        if (!this.modelValue) return null;
        return this.modelValue.query_operator;
      },
      set(value) {
        const payload = this.modelValue || {};
        this.$emit('update:modelValue', { ...payload, query_operator: value });
      },
    },
    custom_attribute_type: {
      get() {
        if (!this.customAttributeType) return '';
        return this.customAttributeType;
      },
      set() {
        const payload = this.modelValue || {};
        this.$emit('update:modelValue', {
          ...payload,
          custom_attribute_type: this.customAttributeType,
        });
      },
    },
  },
  watch: {
    customAttributeType: {
      handler(value) {
        if (!this.modelValue) return;
        if (
          value === 'conversation_attribute' ||
          value === 'contact_attribute'
        ) {
          // eslint-disable-next-line vue/no-mutating-props
          this.modelValue.custom_attribute_type = this.customAttributeType;
          // eslint-disable-next-line vue/no-mutating-props
        } else this.modelValue.custom_attribute_type = '';
      },
      immediate: true,
    },
  },
  methods: {
    optionDisplayLabel(option) {
      if (option == null) return '';
      if (typeof option === 'string') return option;
      if (typeof option === 'number') return String(option);
      return option.name != null ? String(option.name) : String(option.id ?? '');
    },
    removeFilter() {
      this.$emit('removeFilter');
    },
    resetFilter() {
      this.$emit('resetFilter');
    },
    getInputErrorClass(errorMessage) {
      return errorMessage
        ? 'bg-n-ruby-8/20 border-n-ruby-5 dark:border-n-ruby-5'
        : 'bg-n-background border-n-weak dark:border-n-weak';
    },
  },
};
</script>

<!-- eslint-disable vue/no-mutating-props -->
<template>
  <div>
    <div
      class="p-2 border border-solid rounded-lg"
      :class="getInputErrorClass(errorMessage)"
    >
      <div class="flex gap-1">
        <select
          v-if="groupedFilters"
          v-model="attributeKey"
          :title="selectedAttributeTitle"
          class="min-w-0 max-w-[34%] shrink mb-0 mr-1 basis-[min(14rem,34%)]"
          @change="resetFilter()"
        >
          <optgroup
            v-for="(group, i) in filterGroups"
            :key="i"
            :label="group.name"
          >
            <option
              v-for="attribute in group.attributes"
              :key="attribute.key"
              :value="attribute.key"
              :selected="true"
              :title="attribute.name"
            >
              {{ attribute.name }}
            </option>
          </optgroup>
        </select>
        <select
          v-else
          v-model="attributeKey"
          :title="selectedAttributeTitle"
          class="min-w-0 max-w-[34%] shrink mb-0 mr-1 basis-[min(14rem,34%)]"
          @change="resetFilter()"
        >
          <option
            v-for="attribute in filterAttributes"
            :key="attribute.key"
            :value="attribute.key"
            :disabled="attribute.disabled"
            :title="attribute.name"
          >
            {{ attribute.name }}
          </option>
        </select>

        <select
          v-model="filterOperator"
          :title="selectedOperatorTitle"
          class="min-w-0 max-w-[22%] shrink mb-0 mr-1 basis-[min(9rem,22%)]"
        >
          <option
            v-for="(operator, o) in operators"
            :key="o"
            :value="operator.value"
            :title="$t(`FILTER.OPERATOR_LABELS.${operator.value}`)"
          >
            {{ $t(`FILTER.OPERATOR_LABELS.${operator.value}`) }}
          </option>
        </select>

        <div
          v-if="showUserInput"
          class="min-w-0 w-full max-w-full flex-1 mr-1 filter__answer--wrap filter-input-multiselect"
        >
          <div
            v-if="inputType === 'multi_select'"
            class="multiselect-wrap--small min-w-0 w-full"
          >
            <multiselect
              v-model="values"
              track-by="id"
              label="name"
              :placeholder="$t('FORMS.MULTISELECT.SELECT')"
              multiple
              selected-label
              :select-label="$t('FORMS.MULTISELECT.ENTER_TO_SELECT')"
              deselect-label=""
              :max-height="280"
              :options="dropdownValues"
              :allow-empty="false"
            >
              <template #tag="{ option, remove }">
                <span class="multiselect__tag" :title="optionDisplayLabel(option)">
                  <span
                    class="filter-input-multiselect__tag-text inline-block max-w-full whitespace-normal break-words text-left align-middle leading-snug [overflow-wrap:anywhere]"
                  >
                    {{ optionDisplayLabel(option) }}
                  </span>
                  <span
                    role="button"
                    tabindex="0"
                    class="multiselect__tag-icon"
                    :aria-label="$t('FORMS.MULTISELECT.ENTER_TO_REMOVE')"
                    @click.prevent="remove(option)"
                    @keydown.enter.prevent="remove(option)"
                    @keydown.space.prevent="remove(option)"
                  />
                </span>
              </template>
              <template #option="{ option }">
                <span
                  class="block w-full max-w-full whitespace-normal break-words py-0.5 text-left leading-snug [overflow-wrap:anywhere]"
                  :title="optionDisplayLabel(option)"
                >
                  {{ optionDisplayLabel(option) }}
                </span>
              </template>
              <template #noOptions>
                {{ $t('FORMS.MULTISELECT.NO_OPTIONS') }}
              </template>
            </multiselect>
          </div>
          <div
            v-else-if="inputType === 'search_select'"
            class="multiselect-wrap--small min-w-0 w-full"
          >
            <multiselect
              v-model="values"
              track-by="id"
              label="name"
              :placeholder="$t('FORMS.MULTISELECT.SELECT')"
              selected-label
              :select-label="$t('FORMS.MULTISELECT.ENTER_TO_SELECT')"
              deselect-label=""
              :max-height="280"
              :options="dropdownValues"
              :allow-empty="false"
            >
              <template #singleLabel="{ option }">
                <span
                  class="filter-input-multiselect__single-label block w-full max-w-full whitespace-normal break-words py-0.5 text-left leading-snug"
                  :title="optionDisplayLabel(option)"
                >
                  {{ optionDisplayLabel(option) }}
                </span>
              </template>
              <template #option="{ option }">
                <span
                  class="block w-full max-w-full whitespace-normal break-words py-0.5 text-left leading-snug [overflow-wrap:anywhere]"
                  :title="optionDisplayLabel(option)"
                >
                  {{ optionDisplayLabel(option) }}
                </span>
              </template>
              <template #noOptions>
                {{ $t('FORMS.MULTISELECT.NO_OPTIONS') }}
              </template>
            </multiselect>
          </div>
          <div v-else-if="inputType === 'date'" class="multiselect-wrap--small">
            <input
              v-model="values"
              type="date"
              :editable="false"
              class="!mb-0 datepicker"
            />
          </div>
          <input
            v-else
            v-model="values"
            type="text"
            class="!mb-0"
            :placeholder="$t('FILTER.INPUT_PLACEHOLDER')"
          />
        </div>
        <NextButton
          icon="i-lucide-x"
          slate
          ghost
          class="flex-shrink-0"
          @click="removeFilter"
        />
      </div>
      <p v-if="errorMessage" class="filter-error">
        {{ errorMessage }}
      </p>
    </div>

    <div
      v-if="showQueryOperator"
      class="flex items-center justify-center relative my-2.5 mx-0"
    >
      <hr class="absolute w-full border-b border-solid border-n-weak" />
      <select
        v-model="query_operator"
        class="relative w-auto mb-0 bg-n-background text-n-slate-12 border-n-weak"
      >
        <option value="and">
          {{ $t('FILTER.QUERY_DROPDOWN_LABELS.AND') }}
        </option>
        <option value="or">
          {{ $t('FILTER.QUERY_DROPDOWN_LABELS.OR') }}
        </option>
      </select>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.filter__answer--wrap {
  input {
    @apply bg-n-background mb-0 text-n-slate-12 border-n-weak;
  }
}

.filter-error {
  @apply text-n-ruby-9 dark:text-n-ruby-9 block my-1 mx-0;
}

.multiselect {
  @apply mb-0;
}

.filter-input-multiselect :deep(.multiselect) {
  min-width: 0;
  width: 100%;
  max-width: 100%;
}

.filter-input-multiselect :deep(.multiselect__single) {
  white-space: normal;
  overflow: visible;
  text-overflow: clip;
  max-height: none;
  height: auto;
  min-height: 2.375rem;
}

.filter-input-multiselect :deep(.multiselect__option) {
  white-space: normal;
  height: auto;
  min-height: 2.5rem;
}

.filter-input-multiselect :deep(.multiselect__option span) {
  white-space: normal;
  overflow: visible;
  text-overflow: clip;
  width: auto;
  max-width: 100%;
  overflow-wrap: anywhere;
  word-break: break-word;
}

.filter-input-multiselect :deep(.multiselect__content-wrapper) {
  left: 0;
  right: auto;
  width: 100%;
  max-width: 100%;
  min-width: 0;
  box-sizing: border-box;
  @apply bg-n-background;
}

.filter-input-multiselect :deep(.multiselect__content) {
  display: block;
  width: 100%;
  max-width: 100%;
  min-width: 0;
  box-sizing: border-box;
}

.filter-input-multiselect :deep(.multiselect__tag) {
  max-width: 100%;
  white-space: normal;
  height: auto;
  min-height: auto;
}
</style>
